import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Task, Goal, Matter, Project, Client, BreakRecord } from '../lib/db';

interface TimerState {
  isRunning: boolean;
  isPaused: boolean;
  isOnBreak: boolean;
  startTime: Date | null;
  pausedAt: Date | null;
  totalPausedMs: number;
  breakStartTime: Date | null;
  activeBreakId: string | null;
  task: Task | null;
  goal?: Goal;
  matter?: Matter;
  project?: Project;
  client?: Client;
  notes: string;
  timeEntryId?: string;
  userId?: string;
}

interface TimerContextType {
  timerState: TimerState;
  startTimer: (task: Task, goal?: Goal, matter?: Matter, project?: Project, client?: Client, userId?: string) => void;
  stopTimer: () => { startTime: Date; endTime: Date; notes: string } | null;
  startBreak: () => Promise<void>;
  endBreak: () => Promise<void>;
  updateNotes: (notes: string) => void;
  clearTimer: () => void;
  elapsedTime: string;
  breakElapsedTime: string;
  setTimeEntryId: (id: string) => void;
  setUserId: (id: string) => void;
}

const TimerContext = createContext<TimerContextType | undefined>(undefined);

export function useTimer() {
  const context = useContext(TimerContext);
  if (!context) {
    throw new Error('useTimer must be used within a TimerProvider');
  }
  return context;
}

export function TimerProvider({ children }: { children: React.ReactNode }) {
  const [timerState, setTimerState] = useState<TimerState>({
    isRunning: false,
    isPaused: false,
    isOnBreak: false,
    startTime: null,
    pausedAt: null,
    totalPausedMs: 0,
    breakStartTime: null,
    activeBreakId: null,
    task: null,
    notes: '',
  });
  const [elapsedTime, setElapsedTime] = useState<string>('00:00:00');
  const [breakElapsedTime, setBreakElapsedTime] = useState<string>('00:00:00');

  // Update elapsed time every second
  useEffect(() => {
    let intervalId: number;

    if (timerState.isRunning && timerState.startTime && !timerState.isOnBreak) {
      intervalId = window.setInterval(() => {
        const now = new Date();
        const diff = now.getTime() - timerState.startTime!.getTime() - timerState.totalPausedMs;
        const hours = Math.floor(diff / 3600000);
        const minutes = Math.floor((diff % 3600000) / 60000);
        const seconds = Math.floor((diff % 60000) / 1000);
        setElapsedTime(
          `${hours.toString().padStart(2, '0')}:${minutes
            .toString()
            .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
        );
      }, 1000);
    } else if (!timerState.isRunning) {
      setElapsedTime('00:00:00');
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [timerState.isRunning, timerState.startTime, timerState.totalPausedMs, timerState.isOnBreak]);

  // Update break elapsed time
  useEffect(() => {
    let intervalId: number;

    if (timerState.isOnBreak && timerState.breakStartTime) {
      intervalId = window.setInterval(() => {
        const now = new Date();
        const diff = now.getTime() - timerState.breakStartTime!.getTime();
        const hours = Math.floor(diff / 3600000);
        const minutes = Math.floor((diff % 3600000) / 60000);
        const seconds = Math.floor((diff % 60000) / 1000);
        setBreakElapsedTime(
          `${hours.toString().padStart(2, '0')}:${minutes
            .toString()
            .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
        );
      }, 1000);
    } else {
      setBreakElapsedTime('00:00:00');
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [timerState.isOnBreak, timerState.breakStartTime]);

  const startTimer = useCallback((
    task: Task, 
    goal?: Goal, 
    matter?: Matter, 
    project?: Project, 
    client?: Client,
    userId?: string
  ) => {
    setTimerState({
      isRunning: true,
      isPaused: false,
      isOnBreak: false,
      startTime: new Date(),
      pausedAt: null,
      totalPausedMs: 0,
      breakStartTime: null,
      activeBreakId: null,
      task,
      goal,
      matter,
      project,
      client,
      notes: '',
      timeEntryId: undefined,
      userId,
    });
  }, []);

  const stopTimer = useCallback(() => {
    if (!timerState.startTime || !timerState.isRunning) {
      return null;
    }

    const endTime = new Date();
    
    // Round up to the nearest whole minute
    const seconds = endTime.getSeconds();
    const milliseconds = endTime.getMilliseconds();
    
    if (seconds > 0 || milliseconds > 0) {
      // Add the remaining time to get to the next minute
      endTime.setTime(endTime.getTime() + (60 - seconds) * 1000 - milliseconds);
    }

    const result = {
      startTime: timerState.startTime,
      endTime,
      notes: timerState.notes,
    };

    // Stop the timer but keep the task info for reference
    setTimerState(prev => ({
      ...prev,
      isRunning: false,
      startTime: null,
    }));

    return result;
  }, [timerState]);

  const updateNotes = useCallback((notes: string) => {
    setTimerState(prev => ({
      ...prev,
      notes,
    }));
  }, []);

  const clearTimer = useCallback(() => {
    setTimerState({
      isRunning: false,
      isPaused: false,
      isOnBreak: false,
      startTime: null,
      pausedAt: null,
      totalPausedMs: 0,
      breakStartTime: null,
      activeBreakId: null,
      task: null,
      notes: '',
      timeEntryId: undefined,
    });
  }, []);

  const startBreak = useCallback(async () => {
    console.log('startBreak called', { isRunning: timerState.isRunning, isOnBreak: timerState.isOnBreak, userId: timerState.userId });
    if (!timerState.isRunning || timerState.isOnBreak) return;

    // Import db dynamically to avoid circular dependencies
    const { db } = await import('../lib/db');
    
    if (timerState.userId) {
      try {
        const breakRecord = await db.breakRecords.create({
          userId: timerState.userId,
          taskId: timerState.task?.id,
          timeEntryId: timerState.timeEntryId,
        });

        setTimerState(prev => ({
          ...prev,
          isOnBreak: true,
          breakStartTime: new Date(),
          activeBreakId: breakRecord.id,
        }));
      } catch (error) {
        console.error('Error starting break:', error);
      }
    } else {
      console.error('No user ID found in timer state');
    }
  }, [timerState]);

  const endBreak = useCallback(async () => {
    if (!timerState.isOnBreak || !timerState.activeBreakId) return;

    // Import db dynamically to avoid circular dependencies
    const { db } = await import('../lib/db');
    
    if (timerState.userId && timerState.activeBreakId) {
      try {
        await db.breakRecords.endBreak(timerState.activeBreakId, timerState.userId);
        
        const breakDuration = timerState.breakStartTime 
          ? new Date().getTime() - timerState.breakStartTime.getTime()
          : 0;

        setTimerState(prev => ({
          ...prev,
          isOnBreak: false,
          breakStartTime: null,
          activeBreakId: null,
          totalPausedMs: prev.totalPausedMs + breakDuration,
        }));
      } catch (error) {
        console.error('Error ending break:', error);
      }
    }
  }, [timerState]);

  const setTimeEntryId = useCallback((id: string) => {
    setTimerState(prev => ({
      ...prev,
      timeEntryId: id,
    }));
  }, []);

  const setUserId = useCallback((id: string) => {
    setTimerState(prev => ({
      ...prev,
      userId: id,
    }));
  }, []);

  const value: TimerContextType = {
    timerState,
    startTimer,
    stopTimer,
    startBreak,
    endBreak,
    updateNotes,
    clearTimer,
    elapsedTime,
    breakElapsedTime,
    setTimeEntryId,
    setUserId,
  };

  return <TimerContext.Provider value={value}>{children}</TimerContext.Provider>;
}